<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">photo_library</span>
          </div>
          <h4 class="title">{{ $t("returns.photoTypesPage.title") }}</h4>
          <md-button v-if="canManage" class="md-icon-button md-success add-button" @click="openCreate">
            <md-icon>add</md-icon>
          </md-button>
        </md-card-header>

        <md-card-content>
          <p class="ret-pt__hint">{{ $t("returns.photoTypesPage.hint") }}</p>

          <md-table v-model="types" table-header-color="green">
            <md-table-row slot="md-table-row" slot-scope="{ item }">
              <md-table-cell :md-label="$t('returns.table.actions')">
                <md-button v-if="canManage" class="md-icon-button" @click="openEdit(item)"><md-icon>edit</md-icon></md-button>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.photoTypesPage.sortOrder')">{{ item.sortOrder }}</md-table-cell>
              <md-table-cell :md-label="$t('returns.photoTypesPage.name')">{{ item.name || typeLabel(item.code) }}</md-table-cell>
              <md-table-cell :md-label="$t('returns.photoTypesPage.code')"><code>{{ item.code }}</code></md-table-cell>
              <md-table-cell :md-label="$t('returns.photoTypesPage.placement')">{{ $t("returns.photoTypesPage.placements." + (item.placement || "paket")) }}</md-table-cell>
              <md-table-cell :md-label="$t('returns.photoTypesPage.isRequired')">
                <md-icon :class="item.isRequired ? 'ret-ok' : 'ret-muted'">{{ item.isRequired ? "check_circle" : "remove" }}</md-icon>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.photoTypesPage.isActive')">
                <md-icon :class="item.isActive ? 'ret-ok' : 'ret-muted'">{{ item.isActive ? "check_circle" : "cancel" }}</md-icon>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
      </md-card>
    </div>

    <modal v-if="editModal" @close="editModal = false">
      <template slot="header">
        <h4 class="modal-title">{{ form.id ? $t("returns.photoTypesPage.edit") : $t("returns.photoTypesPage.create") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="editModal = false">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <md-field>
          <label>{{ $t("returns.photoTypesPage.code") }}</label>
          <md-input v-model="form.code" :disabled="!!form.id"></md-input>
        </md-field>
        <p class="ret-pt__hint">{{ $t("returns.photoTypesPage.codeHint") }}</p>

        <!-- Freie Bezeichnung (P8): leer = Uebersetzung des Codes -->
        <md-field>
          <label>{{ $t("returns.photoTypesPage.nameField") }}</label>
          <md-input v-model="form.name" :placeholder="typeLabel(form.code)"></md-input>
        </md-field>

        <!-- Wo die Kachel im Erfassungs-Flow erscheint (P8.1) -->
        <md-field>
          <label>{{ $t("returns.photoTypesPage.placement") }}</label>
          <md-select v-model="form.placement">
            <md-option v-for="pl in placements" :key="pl" :value="pl">{{ $t("returns.photoTypesPage.placements." + pl) }}</md-option>
          </md-select>
        </md-field>

        <md-field>
          <label>{{ $t("returns.photoTypesPage.sortOrder") }}</label>
          <md-input v-model.number="form.sortOrder" type="number"></md-input>
        </md-field>

        <div class="ret-pt__switches">
          <md-switch v-model="form.isRequired">{{ $t("returns.photoTypesPage.isRequired") }}</md-switch>
          <md-switch v-model="form.isActive">{{ $t("returns.photoTypesPage.isActive") }}</md-switch>
        </div>
      </template>

      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="editModal = false">{{ $t("returns.detail.close") }}</md-button>
        <md-button class="md-success" :disabled="saving || !form.code" @click="save">{{ $t("returns.photoTypesPage.save") }}</md-button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Modal } from "@/components";
import ReturnsService from "@/services/returns.service";
import { hasPermission } from "@/mixins/auth";

// Stammdaten der Foto-Slots: bestimmt, welche Fotos die Lager-App verlangt.
// Der Code liegt neutral in der DB, die Beschriftung kommt aus der i18n
// (returns.photoTypes.<code>) - unbekannte Codes zeigen den Code selbst.
export default {
  name: "ReturnPhotoTypes",
  components: { Modal },
  data() {
    return {
      types: [],
      editModal: false,
      saving: false,
      placements: ["paket", "artikel", "abschluss", "hidden"],
      form: { id: null, code: "", name: "", placement: "paket", sortOrder: 10, isRequired: false, isActive: true },
    };
  },
  computed: {
    canManage() {
      return hasPermission("Write", "ReturnPhotoTypes", "Edit");
    },
  },
  created() {
    this.load();
  },
  methods: {
    typeLabel(code) {
      const key = "returns.photoTypes." + code;
      return this.$te(key) ? this.$t(key) : code;
    },
    async load() {
      this.types = await ReturnsService.allPhotoTypes();
    },
    openCreate() {
      const maxOrder = this.types.reduce((m, t) => Math.max(m, t.sortOrder || 0), 0);
      this.form = { id: null, code: "", name: "", placement: "paket", sortOrder: maxOrder + 10, isRequired: false, isActive: true };
      this.editModal = true;
    },
    openEdit(item) {
      this.form = { placement: "paket", name: "", ...item };
      this.editModal = true;
    },
    async save() {
      this.saving = true;
      try {
        await ReturnsService.savePhotoType(this.form);
        this.editModal = false;
        await this.load();
        this.$notify({ timeout: 2500, message: this.$t("returns.photoTypesPage.saved"), icon: "check", horizontalAlign: "right", verticalAlign: "top", type: "success" });
      } catch (err) {
        const reason = (err.response && err.response.data && err.response.data.reason) || "load_failed";
        const key = "returns.errors." + reason;
        this.$notify({ timeout: 3000, message: this.$te(key) ? this.$t(key) : reason, icon: "error", horizontalAlign: "right", verticalAlign: "top", type: "danger" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ret-pt__hint {
  color: #9e9e9e;
  font-size: 13px;
  text-align: left;
}

.ret-pt__switches {
  display: flex;
  gap: 24px;
  margin-top: 10px;
}

.ret-ok {
  color: #7f915a !important;
}

.ret-muted {
  color: #bdbdbd !important;
}
</style>
